import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CreatePodInstance } from "@/components/pods/CreatePodInstance";
import { PageLayout } from "@/components/PageLayout";
import { ErrorBanner } from "@/components/ErrorBanner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faListCheck,
  faPlus,
  faUsers,
  faBook,
  faChartLine,
  faList,
  faTasks,
  faChartBar,
  faExclamationTriangle,
  faClockRotateLeft,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import type { Tab } from "@/components/ui/tabs/Tabs";
import { PodDashboard } from "@/components/pods/PodDashboard";
import { InstanceTasks } from "@/components/pods/InstanceTasks";
import { PodInstanceList } from "@/components/pods/PodInstanceList";
import { Actions } from "@/components/pods/Actions";
import { LogsOverview } from "@/components/pods/LogsOverview";
import { getPodTemplate } from "@/services/api/agentic";
import type { PodTemplate as ApiPodTemplate } from "@/services/api/agentic/types";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import LoadingPlaceholder from "@/components/ui/loading-placeholder/LoadingPlaceholder";
import { useWebSocket } from "@/context/WebSocketContext";
import { WS_CHANNELS } from "@/utils/websocket";
import { useSetWindowTitle, useIsOsMode } from "@/components/ui/os-layout";
import { Button } from "@/components/ui/button/Button";

// Define PodTemplateTab locally based on the API response structure
interface PodTemplateTab {
  name: string;
  type: string;
  icon?: string;
  color?: string;
}

// Extend ApiPodTemplate to include the tabs property if it's missing
interface PodTemplate extends ApiPodTemplate {
  pod_settings?: {
    icon?: string;
    pod_instance_name?: string;
    pod_template_tabs?: PodTemplateTab[];
    pod_instance_tabs?: Array<{
      name: string;
      type: string;
      icon?: string;
      color?: string;
    }>;
  };
  tabs?: PodTemplateTab[]; // Keep this for backward compatibility or remove if API guarantees new structure
}

export const meta = () => {
  return [
    { title: "Pod | Ani Tech" },
    { name: "description", content: "Pod details and management" },
  ];
};

// Main component that accepts templateId prop (for OS mode)
function PodComponent({ templateId: propTemplateId }: { templateId?: string }) {
  const { id: paramsTemplateId } = useParams<{ id: string }>();
  const templateId = propTemplateId || paramsTemplateId;
  const setWindowTitle = useSetWindowTitle();
  const [searchParams, setSearchParams] = useSearchParams();
  const { lastMessage, subscribe, unsubscribe } = useWebSocket();
  const isOsMode = useIsOsMode();

  const [topLevelError, setTopLevelError] = useState<Error | null>(null);
  // In OS mode, don't use URL params for initial state - let the useEffect handle it
  const [activeTab, setActiveTab] = useState<string>(() => {
    if (isOsMode) return "pending"; // Will be set by useEffect when tabs load
    return searchParams.get("tab") || "overview";
  });

  const [podTemplate, setPodTemplate] = useState<PodTemplate | null>(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState<boolean>(false);
  const [templateError, setTemplateError] = useState<Error | null>(null);
  const [callToActionsCount, setCallToActionsCount] = useState<number>(0);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Icon mapping for dynamic icon rendering
  const iconMap: Record<string, IconDefinition> = {
    "fa-chart-line": faChartLine,
    "fa-chart-bar": faChartBar,
    "fa-list": faList,
    "fa-tasks": faTasks,
    "fa-book": faBook,
    "fa-users": faUsers,
    "fa-list-check": faListCheck,
    "fa-exclamation-triangle": faExclamationTriangle,
    "fa-clock-rotate-left": faClockRotateLeft,
  };

  const getIconByName = (name?: string): IconDefinition | undefined => {
    if (!name) return undefined;
    return iconMap[name];
  };

  useEffect(() => {
    if (templateId) {
      setIsLoadingTemplate(true);
      setTemplateError(null);
      getPodTemplate(templateId)
        .then((data) => {
          setPodTemplate(data);
          // Update document title
          if (data?.name) {
            document.title = `${data.name} | Ani Tech`;
            setWindowTitle(data.name);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch pod template:", err);
          setTemplateError(err);
          setTopLevelError(
            new Error(`Failed to load template: ${err.message}`)
          );
        })
        .finally(() => {
          setIsLoadingTemplate(false);
        });
    }
  }, [templateId, setWindowTitle]);

  // Sync activeTab with URL query parameter (only in non-OS mode)
  useEffect(() => {
    if (isOsMode) return; // Skip URL sync in OS mode
    const tabParam = searchParams.get("tab");
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [searchParams, activeTab, isOsMode]);

  const handleOpenDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const handleProjectCreated = useCallback(() => {
    // If creating a project should refresh pod instances (indirectly), add:
    // if (podInstanceListRef.current) {
    //   podInstanceListRef.current.refreshPodInstances();
    // }
  }, []);

  // Fetch functions for Actions component
  const fetchCallToActionsByTemplate = useCallback(
    async (templateIdParam: string): Promise<any[]> => {
      const { getCallToActionsByTemplate } = await import(
        "@/services/api/agentic"
      );
      const response = await getCallToActionsByTemplate(templateIdParam, {
        limit: 500,
      });
      // Filter to only show active CTAs (live and processing), exclude completed and cancelled
      const activeCtas = Array.isArray(response)
        ? response.filter(
            (cta: any) => cta.status === "live" || cta.status === "processing"
          )
        : [];
      return activeCtas;
    },
    []
  );

  const fetchPodInstancesForTemplate = useCallback(
    async (
      templateIdParam: string
    ): Promise<Array<{ id: string; name: string }>> => {
      const { getPodInstances } = await import("@/services/api/agentic");
      const response = await getPodInstances({ template_id: templateIdParam });
      const instances = Array.isArray(response) ? response : [];
      return instances.map((instance: any) => ({
        id: instance.id || instance._id,
        name: instance.name,
      }));
    },
    []
  );

  // Fetch call-to-actions count
  const refreshCallToActionsCount = useCallback(async () => {
    if (templateId) {
      try {
        const ctas = await fetchCallToActionsByTemplate(templateId);
        setCallToActionsCount(ctas.length);
      } catch (err) {
        console.error("Failed to fetch call-to-actions count:", err);
      }
    }
  }, [templateId, fetchCallToActionsByTemplate]);

  useEffect(() => {
    refreshCallToActionsCount();
  }, [refreshCallToActionsCount]);

  // WebSocket subscription for real-time CTA count updates
  useEffect(() => {
    const channel = WS_CHANNELS.CALL_TO_ACTIONS;
    subscribe(channel);

    return () => {
      unsubscribe(channel);
    };
  }, [subscribe, unsubscribe]);

  // Handle WebSocket messages to update count
  useEffect(() => {
    const message: any = lastMessage.get(WS_CHANNELS.CALL_TO_ACTIONS);

    if (message && message.full_document) {
      // Refresh count when CTAs change
      refreshCallToActionsCount();
    }
  }, [lastMessage, refreshCallToActionsCount]);

  const tabRightSlot = useMemo(
    () => (
      <Button onClick={handleOpenDialog} color="indigo">
        <span data-slot="icon">
          <FontAwesomeIcon icon={faPlus} />
        </span>
        New {podTemplate?.pod_settings?.pod_instance_name || "Case"}
      </Button>
    ),
    [handleOpenDialog, podTemplate]
  );

  const tabs: Tab[] = useMemo(() => {
    if (
      !podTemplate ||
      !podTemplate.pod_settings ||
      !podTemplate.pod_settings.pod_template_tabs ||
      podTemplate.pod_settings.pod_template_tabs.length === 0
    ) {
      return [];
    }

    const dashboardComponent = (
      <PodDashboard
        templateId={templateId}
        podInstanceName={podTemplate?.pod_settings?.pod_instance_name}
      />
    );

    const instanceListComponent = (
      <div className="w-full px-6">
        <PodInstanceList templateId={templateId} podTemplate={podTemplate} />
      </div>
    );

    const actionsComponent = templateId ? (
      <Actions
        templateId={templateId}
        fetchCallToActions={fetchCallToActionsByTemplate}
        fetchPodInstances={fetchPodInstancesForTemplate}
        podTemplate={podTemplate || undefined}
      />
    ) : null;

    const tabComponentMap: Record<
      string,
      {
        component: React.ReactNode;
        icon?: React.ReactNode;
      }
    > = {
      dashboard: {
        component: dashboardComponent,
        icon: <FontAwesomeIcon icon={faBook} fixedWidth />,
      },
      overview: {
        component: dashboardComponent,
        icon: <FontAwesomeIcon icon={faBook} fixedWidth />,
      },
      "instance-list": {
        component: instanceListComponent,
        icon: <FontAwesomeIcon icon={faUsers} fixedWidth />,
      },
      "processing-queue": {
        component: instanceListComponent,
        icon: <FontAwesomeIcon icon={faUsers} fixedWidth />,
      },
      queue: {
        component: instanceListComponent,
        icon: <FontAwesomeIcon icon={faUsers} fixedWidth />,
      },
      "instance-tasks": {
        component: <InstanceTasks />,
        icon: <FontAwesomeIcon icon={faListCheck} fixedWidth />,
      },
      actions: {
        component: actionsComponent,
        icon: <FontAwesomeIcon icon={faExclamationTriangle} fixedWidth />,
      },
      exceptions: {
        component: actionsComponent,
        icon: <FontAwesomeIcon icon={faExclamationTriangle} fixedWidth />,
      },
      logs: {
        component: <LogsOverview templateId={templateId} />,
        icon: <FontAwesomeIcon icon={faClockRotateLeft} fixedWidth />,
      },
    };

    const newTabs = podTemplate.pod_settings.pod_template_tabs
      .map((apiTab: PodTemplateTab): Tab | null => {
        const tabConfig = tabComponentMap[apiTab.type];
        if (!tabConfig) {
          console.warn(
            `Unknown tab type: ${apiTab.type}. Available types: ${Object.keys(
              tabComponentMap
            ).join(", ")}`
          );
          return null;
        }

        const apiIconDefinition = getIconByName(apiTab.icon);
        const displayIcon = apiIconDefinition ? (
          <FontAwesomeIcon icon={apiIconDefinition} fixedWidth />
        ) : (
          tabConfig.icon
        );

        return {
          id: apiTab.type,
          label: apiTab.name,
          icon: displayIcon,
          content: tabConfig.component,
          currentIcon: apiTab.icon,
          count:
            apiTab.type === "actions" || apiTab.type === "exceptions"
              ? callToActionsCount
              : undefined,
        };
      })
      .filter((tab): tab is Tab => tab !== null);

    return newTabs;
  }, [
    podTemplate,
    templateId,
    fetchCallToActionsByTemplate,
    fetchPodInstancesForTemplate,
    callToActionsCount,
  ]);

  // Get list of valid tab IDs for validation
  const validTabIds = useMemo(() => tabs.map((t) => t.id), [tabs]);

  // Handle initial tab selection - only run when tabs first load or change
  useEffect(() => {
    if (validTabIds.length === 0) return;

    // Only redirect if the current activeTab is NOT in the valid tabs
    if (!validTabIds.includes(activeTab)) {
      const defaultTabType =
        podTemplate?.pod_settings?.pod_template_tabs?.[0]?.type || "dashboard";

      if (validTabIds.includes(defaultTabType)) {
        setActiveTab(defaultTabType);
      } else {
        setActiveTab(validTabIds[0]);
      }
    }
    // Intentionally only depend on validTabIds changing, not activeTab
    // This prevents the effect from running when user clicks a tab
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validTabIds.join(","), podTemplate]);

  const handleTabChange = useCallback(
    (tabId: string) => {
      setActiveTab(tabId);
      // Only update URL params in non-OS mode
      if (!isOsMode) {
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("tab", tabId);
          return newParams;
        });
      }
    },
    [setSearchParams, isOsMode]
  );

  const pageTitle = useMemo(() => {
    if (isLoadingTemplate) return "Loading Pod...";
    if (podTemplate) return podTemplate.name;
    if (templateError) return "Error Loading Pod";
    return "Pod";
  }, [podTemplate, isLoadingTemplate, templateError]);

  return (
    <>
      <PageLayout
        title={pageTitle}
        tabs={tabs}
        staticTabs={true}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        disableIconEdit={true}
        tabRightSlot={tabRightSlot}
      >
        {(templateError || topLevelError) && (
          <ErrorBanner
            message={
              templateError?.message ||
              topLevelError?.message ||
              "An unexpected error occurred."
            }
          />
        )}
        {isLoadingTemplate && !podTemplate && (
          <div className="p-6">
            <LoadingPlaceholder pattern="card" count={1} />
          </div>
        )}
      </PageLayout>

      <CreatePodInstance
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        onSuccess={handleProjectCreated}
        instanceName={
          podTemplate?.pod_settings?.pod_instance_name || "Pod Instance"
        }
        routeTemplateId={templateId}
      />
    </>
  );
}

// Default export for Remix route (doesn't receive props)
export default function Projects() {
  return <PodComponent />;
}

// Named export for OS mode (receives templateId prop)
export { PodComponent as PodRoute };

